import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tab,
  Tabs,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  AlertTitle
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Send as SendIcon,
  GetApp as GetAppIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../../request/http-common';
import JointAccountDeposit from './JointAccountDeposit';
import JointAccountWithdraw from './JointAccountWithdraw';
import InviteMember from './InviteMember';

const JointAccountDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentTab, setCurrentTab] = useState(0);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [withdrawDescription, setWithdrawDescription] = useState('');
  const [transferDescription, setTransferDescription] = useState('');
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [inviteMemberDialogOpen, setInviteMemberDialogOpen] = useState(false);

  const { data: jointAccount, isLoading, error, refetch } = useQuery(
    ['jointAccount', id],
    async () => {
      const response = await apiClient.get(`/api/joint-accounts/${id}`);
      return response.data.joint_account;
    }
  );

  const approveMutation = useMutation(
    async ({ transactionId, action, comment }) => {
      const response = await apiClient.post(`/api/joint-transactions/${transactionId}/approve`, {
        action,
        comment
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['jointAccount', id]);
        queryClient.invalidateQueries('jointAccounts');
        queryClient.invalidateQueries('pendingApprovals');
      }
    }
  );

  const transferMutation = useMutation(
    async (transferData) => {
      const response = await apiClient.post(`/api/joint-accounts/${id}/transactions`, {
        transaction_type: 'transfer',
        amount: parseFloat(transferData.amount),
        description: transferData.description || 'Transfer to personal account',
        metadata: {
          transfer_type: 'to_personal_account',
          requires_approval: true
        }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['jointAccount', id]);
        queryClient.invalidateQueries('jointAccounts');
        setShowTransferDialog(false);
        setTransferAmount('');
        setTransferDescription('');
      },
      onError: (error) => {
        console.error('Transfer creation failed:', error);
      }
    }
  );

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'suspended': return 'warning';
      case 'closed': return 'error';
      default: return 'default';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return '#F7931A';
      case 'admin': return '#10B981';
      case 'member': return '#3B82F6';
      case 'viewer': return '#6C7293';
      default: return '#6C7293';
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleDialogSuccess = () => {
    refetch(); // Refresh account data
  };

  const handleApprove = (transactionId) => {
    approveMutation.mutate({ transactionId, action: 'approved' });
  };

  const handleReject = (transactionId) => {
    approveMutation.mutate({ transactionId, action: 'rejected' });
  };

  const handleTransfer = () => {
    setShowTransferDialog(true);
  };

  const handleTransferSubmit = () => {
    if (!transferAmount || parseFloat(transferAmount) <= 0) {
      return;
    }

    transferMutation.mutate({
      amount: transferAmount,
      description: transferDescription
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load joint account details. Please try again.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2, pb: { xs: 12, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 3,
        gap: 2
      }}>
        <IconButton 
          onClick={() => navigate('/joint-accounts')}
          sx={{ color: '#F7931A' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ 
          fontWeight: 700, 
          color: '#FFFFFF',
          flex: 1
        }}>
          {jointAccount?.account_name}
        </Typography>
        <Chip
          label={jointAccount?.status}
          color={getStatusColor(jointAccount?.status)}
          sx={{ fontSize: '0.875rem' }}
        />
      </Box>

      {/* Account Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalanceIcon sx={{ fontSize: 48, color: '#F7931A', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#10B981', fontWeight: 700, mb: 1 }}>
                {formatPrice(jointAccount?.balance)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Total Balance
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 48, color: '#3B82F6', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                {jointAccount?.members?.length || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Active Members
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <HistoryIcon sx={{ fontSize: 48, color: '#FFB800', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                {jointAccount?.transactions?.length || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Transactions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 3,
        flexWrap: 'wrap'
      }}>
        {jointAccount?.status === 'pending' && (
          <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
            <AlertTitle>Account Pending Approval</AlertTitle>
            This joint account is pending admin approval. Transaction features will be enabled once approved.
          </Alert>
        )}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDepositDialogOpen(true)}
          disabled={jointAccount?.status === 'pending'}
          sx={{
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
            },
            '&:disabled': {
              background: '#6C7293',
              color: '#FFFFFF'
            }
          }}
        >
          Deposit Funds
        </Button>
        <Button
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={() => setWithdrawDialogOpen(true)}
          disabled={jointAccount?.status === 'pending'}
          sx={{
            borderColor: '#F84960',
            color: '#F84960',
            '&:hover': {
              borderColor: '#DC2626',
              color: '#DC2626',
              backgroundColor: 'rgba(248, 73, 96, 0.1)'
            },
            '&:disabled': {
              borderColor: '#6C7293',
              color: '#6C7293'
            }
          }}
        >
          Withdraw
        </Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleTransfer}
          disabled={jointAccount?.status === 'pending'}
          sx={{
            background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #E8851A 0%, #E8A800 100%)'
            },
            '&:disabled': {
              background: '#6C7293',
              color: '#FFFFFF'
            }
          }}
        >
          Transfer Funds
        </Button>

      </Box>

      {/* Tabs */}
      <Paper sx={{ 
        background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
        border: '1px solid #2B3139',
        mb: 3
      }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              color: '#6C7293',
              '&.Mui-selected': {
                color: '#F7931A'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#F7931A'
            }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Members" />
          <Tab label="Transactions" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Card sx={{
          background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
          border: '1px solid #2B3139'
        }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 2 }}>
              Account Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                  Account Number
                </Typography>
                <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
                  #{jointAccount?.account_number}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                  Approval Type
                </Typography>
                <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
                  {jointAccount?.approval_type?.replace('_', ' ').toUpperCase()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                  Minimum Approval Amount
                </Typography>
                <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
                  {formatPrice(jointAccount?.minimum_approval_amount)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                  Created Date
                </Typography>
                <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
                  {new Date(jointAccount?.created_at).toLocaleDateString()}
                </Typography>
              </Grid>
              {jointAccount?.description && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                    {jointAccount.description}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {currentTab === 1 && (
        <Card sx={{
          background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
          border: '1px solid #2B3139'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                Members ({jointAccount?.members?.length || 0})
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                size="small"
                onClick={() => setInviteMemberDialogOpen(true)}
                sx={{
                  borderColor: '#F7931A',
                  color: '#F7931A',
                  '&:hover': {
                    borderColor: '#FFB800',
                    backgroundColor: 'rgba(247, 147, 26, 0.1)'
                  }
                }}
              >
                Invite Member
              </Button>
            </Box>
            <List>
              {jointAccount?.members?.map((member, index) => (
                <React.Fragment key={member.id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ 
                        bgcolor: getRoleColor(member.role),
                        width: 48,
                        height: 48
                      }}>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ color: '#FFFFFF' }}>
                            {member.user?.name || member.user?.email}
                          </Typography>
                          <Chip
                            label={member.role}
                            size="small"
                            sx={{
                              backgroundColor: getRoleColor(member.role),
                              color: '#FFFFFF',
                              fontSize: '0.75rem'
                            }}
                          />
                          {member.status === 'pending' && (
                            <Chip
                              label="Pending"
                              size="small"
                              color="warning"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ color: '#6C7293' }}>
                          {member.user?.email}
                          {member.joined_at && (
                            <>
                              {' • '}
                              Joined {new Date(member.joined_at).toLocaleDateString()}
                            </>
                          )}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < (jointAccount?.members?.length || 0) - 1 && (
                    <Divider sx={{ borderColor: '#2B3139' }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {currentTab === 2 && (
        <Card sx={{
          background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
          border: '1px solid #2B3139'
        }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 2 }}>
              Recent Transactions
            </Typography>
            {jointAccount?.transactions?.length > 0 ? (
              <List>
                {jointAccount.transactions.slice(0, 10).map((transaction, index) => {
                  const isPendingWithdrawal = (transaction.transaction_type === 'withdrawal' || transaction.transaction_type === 'transfer') && transaction.status === 'pending';
                  const currentUser = JSON.parse(localStorage.getItem('user')) || {};
                  const hasUserApproved = transaction.approvals?.some(approval => 
                    approval.action === 'approved' && approval.approver?.id === currentUser?.id
                  );
                  const hasUserRejected = transaction.approvals?.some(approval => 
                    approval.action === 'rejected' && approval.approver?.id === currentUser?.id
                  );
                  
                  return (
                    <React.Fragment key={transaction.id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="subtitle1" sx={{ color: '#FFFFFF' }}>
                                {transaction.transaction_type?.charAt(0).toUpperCase() + transaction.transaction_type?.slice(1)}
                              </Typography>
                              <Typography variant="subtitle1" sx={{ 
                                color: (transaction.transaction_type === 'deposit' || transaction.transaction_type === 'transfer') ? '#10B981' : '#F84960',
                                fontWeight: 600
                              }}>
                                {(transaction.transaction_type === 'deposit' || transaction.transaction_type === 'transfer') ? '+' : '-'}{formatPrice(transaction.amount)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                                {transaction.description || 'No description'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#6C7293' }}>
                                {new Date(transaction.created_at).toLocaleString()} • Status: {transaction.status}
                              </Typography>
                              
                              {/* Approval Status for Withdrawal and Transfer Transactions */}
                              {(transaction.transaction_type === 'withdrawal' || transaction.transaction_type === 'transfer') && (
                                <Box sx={{ mt: 1 }}>
                                                                     {transaction.status === 'pending' ? (
                                     <Typography variant="caption" sx={{ color: '#F7931A', fontWeight: 600 }}>
                                       Pending Member Approval
                                     </Typography>
                                   ) : transaction.status === 'processing' ? (
                                     <Typography variant="caption" sx={{ color: '#3B82F6', fontWeight: 600 }}>
                                       Processing - Awaiting Admin Approval
                                     </Typography>
                                   ) : (
                                     <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 600 }}>
                                       {transaction.status === 'completed' ? 'Completed' : 'Processed'}
                                     </Typography>
                                   )}
                                  
                                  {/* Show Approval Details */}
                                  {transaction.approvals && transaction.approvals.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" sx={{ color: '#6C7293', fontWeight: 600 }}>
                                        Approvals ({transaction.approvals.length}):
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        {transaction.approvals.map((approval, idx) => (
                                          <Chip
                                            key={idx}
                                            label={`${approval.approver?.name}: ${approval.action}`}
                                            color={approval.action === 'approved' ? 'success' : 'error'}
                                            size="small"
                                            sx={{ 
                                              fontSize: '0.7rem',
                                              height: '20px',
                                              '& .MuiChip-label': {
                                                px: 1
                                              }
                                            }}
                                          />
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                  
                                                                     {/* Show Required vs Received Approvals for Pending Transactions */}
                                   {transaction.status === 'pending' && (
                                     <Box sx={{ mt: 1 }}>
                                       <Typography variant="caption" sx={{ color: '#6C7293' }}>
                                         Required: {jointAccount?.active_members_count || 0} members • 
                                         Received: {transaction.approvals?.filter(a => a.action === 'approved').length || 0} approvals
                                       </Typography>
                                       <Typography variant="caption" sx={{ color: '#3B82F6', display: 'block', mt: 0.5 }}>
                                         ⚡ Sent for admin approval when all members approve
                                       </Typography>
                                     </Box>
                                   )}
                                   
                                   {/* Show Processing Status */}
                                   {transaction.status === 'processing' && (
                                     <Box sx={{ mt: 1 }}>
                                       <Typography variant="caption" sx={{ color: '#3B82F6', fontWeight: 600 }}>
                                         ✅ All members approved - Awaiting final admin approval
                                       </Typography>
                                     </Box>
                                   )}
                                </Box>
                              )}
                            </Box>
                          }
                        />
                        
                        {/* Approval Buttons for Pending Withdrawals and Transfers */}
                        {isPendingWithdrawal && (
                          <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                            {hasUserApproved ? (
                              <Chip
                                label="Approved"
                                color="success"
                                icon={<CheckIcon />}
                                size="small"
                              />
                            ) : hasUserRejected ? (
                              <Chip
                                label="Rejected"
                                color="error"
                                icon={<CloseIcon />}
                                size="small"
                              />
                            ) : (
                              <>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<CheckIcon />}
                                  onClick={() => handleApprove(transaction.id)}
                                  disabled={approveMutation.isLoading}
                                  sx={{
                                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                                    }
                                  }}
                                >
                                  Approve
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<CloseIcon />}
                                  onClick={() => handleReject(transaction.id)}
                                  disabled={approveMutation.isLoading}
                                  sx={{
                                    borderColor: '#F84960',
                                    color: '#F84960',
                                    '&:hover': {
                                      borderColor: '#DC2626',
                                      backgroundColor: 'rgba(248, 73, 96, 0.1)'
                                    }
                                  }}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                          </Box>
                        )}
                      </ListItem>
                      {index < Math.min(9, (jointAccount?.transactions?.length || 0) - 1) && (
                        <Divider sx={{ borderColor: '#2B3139' }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <HistoryIcon sx={{ fontSize: 64, color: '#6C7293', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 1 }}>
                  No Transactions Yet
                </Typography>
                <Typography variant="body2" sx={{ color: '#6C7293' }}>
                  Start by making a deposit to this joint account
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog Components */}
      <JointAccountDeposit
        open={depositDialogOpen}
        onClose={() => setDepositDialogOpen(false)}
        jointAccountId={id}
        onSuccess={handleDialogSuccess}
      />

      <JointAccountWithdraw
        open={withdrawDialogOpen}
        onClose={() => setWithdrawDialogOpen(false)}
        jointAccount={jointAccount}
        onSuccess={handleDialogSuccess}
      />

      <InviteMember
        open={inviteMemberDialogOpen}
        onClose={() => setInviteMemberDialogOpen(false)}
        jointAccountId={id}
        onSuccess={handleDialogSuccess}
      />

      {/* Transfer Dialog */}
      <Dialog
        open={showTransferDialog}
        onClose={() => setShowTransferDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#FFFFFF', 
          borderBottom: '1px solid #2B3139',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <SendIcon sx={{ color: '#F7931A' }} />
          Transfer to Personal Account
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#6C7293', mb: 2 }}>
              Transfer funds from this joint account to your personal account. This transaction will require approval from all members.
            </Typography>
            
            <Card sx={{
              background: 'linear-gradient(135deg, #1E2328 0%, #2B3139 100%)',
              border: '1px solid #3B4148',
              mb: 3
            }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                  Available Balance
                </Typography>
                <Typography variant="h5" sx={{ color: '#10B981', fontWeight: 700 }}>
                  {formatPrice(jointAccount?.balance)}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <TextField
            fullWidth
            label="Transfer Amount"
            type="number"
            value={transferAmount}
            onChange={(e) => setTransferAmount(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#2B3139' },
                '&:hover fieldset': { borderColor: '#3B4148' },
                '&.Mui-focused fieldset': { borderColor: '#F7931A' }
              },
              '& .MuiInputLabel-root': { color: '#6C7293' },
              '& .MuiInputBase-input': { color: '#FFFFFF' }
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
          />

          <TextField
            fullWidth
            label="Description (Optional)"
            value={transferDescription}
            onChange={(e) => setTransferDescription(e.target.value)}
            multiline
            rows={3}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#2B3139' },
                '&:hover fieldset': { borderColor: '#3B4148' },
                '&.Mui-focused fieldset': { borderColor: '#F7931A' }
              },
              '& .MuiInputLabel-root': { color: '#6C7293' },
              '& .MuiInputBase-input': { color: '#FFFFFF' }
            }}
          />

          <Alert severity="info" sx={{ mb: 3, bgcolor: 'rgba(59, 130, 246, 0.1)', borderColor: '#3B82F6' }}>
            <AlertTitle>Approval Required</AlertTitle>
            This transfer will require approval from all joint account members before it can be processed.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #2B3139' }}>
          <Button
            onClick={() => setShowTransferDialog(false)}
            sx={{
              color: '#6C7293',
              '&:hover': { color: '#FFFFFF' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTransferSubmit}
            disabled={!transferAmount || parseFloat(transferAmount) <= 0 || parseFloat(transferAmount) > (jointAccount?.balance || 0) || transferMutation.isLoading}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #E8851A 0%, #E8A800 100%)'
              },
              '&:disabled': {
                background: '#2B3139',
                color: '#6C7293'
              }
            }}
          >
            {transferMutation.isLoading ? 'Processing...' : 'Initiate Transfer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JointAccountDetails; 